import './assets/main.scss'

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { datadogRum } from '@datadog/browser-rum'
import { PrevioDesignSystem } from '@previo/design-system'
import VueGtagPlugin from 'vue-gtag'
import { i18n, useLocale } from '@previo/core'
import VueQRCodeComponent from 'vue-qrcode-component-v3'
import { FeatureFlag } from '@previo/feature-flags'
import { RealtimePlugin, Provider } from '@previo/realtime'
import App from './App.vue'
import router from './router'
import { Environment } from '~/Environment'
import messages from '~/locales'
import { serviceWorkerHelper } from '~/plugins/serviceWorkerHelper'
import { configurePushNotifications } from '~/plugins/pushNotifications'
import { configureDayjs } from '~/plugins/dayjs'
import { chunkLoadRecovery } from '~/plugins/chunkLoadRecovery'

const pinia = createPinia()
const app = createApp(App)

useLocale().setMessages(messages)

app.use(pinia)
app.use(i18n)
app.use(router)
app.use(PrevioDesignSystem)
app.use(configureDayjs)
app.use(
  VueGtagPlugin,
  {
    config: {
      id: import.meta.env.PMS_FE_GOOGLE_ANALYTICS_4_KEY,
    },
  },
  router,
)
app.use(serviceWorkerHelper)
app.use(chunkLoadRecovery)
app.use(FeatureFlag, {
  clientSideID: import.meta.env.PMS_FE_FF_CLIENT_SIDE_ID,
  contextKind: import.meta.env.PMS_FE_FF_CONTEXT_KIND,
  provider: import.meta.env.PMS_FE_FF_PROVIDER,
})

app.component('QrCode', VueQRCodeComponent)

configurePushNotifications(app, {
  pusherBeamsInstanceId: import.meta.env.PMS_FE_PUSHER_BEAMS_INSTANCE_ID,
})

app.mount('#app')

const hideLoader = async () => {
  try {
    await router.isReady()
  } finally {
    const loader = document.getElementById('init-loader')

    if (loader) {
      loader.classList.add('hide')

      // Remove #init-loader div from DOM
      setTimeout(() => {
        loader.remove()
      }, 300)
    }
  }
}

hideLoader()

if (Environment.isEnvLikeProduction()) {
  datadogRum.init({
    applicationId: import.meta.env.PMS_FE_DATADOG_APPLICATION_ID,
    clientToken: import.meta.env.PMS_FE_DATADOG_CLIENT_TOKEN,
    version: import.meta.env.PMS_FE_APP_VERSION,
    site: 'datadoghq.eu',
    service: 'previo2_pms_fe',
    env: Environment.SELECTED,
    sessionSampleRate: 5,
    sessionReplaySampleRate: 0,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
  })
}

switch (import.meta.env.PMS_FE_REALTIME_PROVIDER) {
  case Provider.PUSHER:
    app.use(RealtimePlugin, {
      provider: Provider.PUSHER,
      dsn: import.meta.env.PMS_FE_REALTIME_DSN,
    })

    break

  case Provider.CENTRIFUGO_DEV_ONLY:
    app.use(RealtimePlugin, {
      provider: Provider.CENTRIFUGO_DEV_ONLY,
      dsn: import.meta.env.PMS_FE_REALTIME_DSN,
      project: 'pms',
    })

    break
}
